import { useMemo, useRef } from 'react'

import {
  CloseButton,
  Combobox,
  Group,
  Pill,
  Text,
  TextInput,
  useCombobox,
} from '@mantine/core'

import { useAdminFundAssets } from '@repo/common/queries/admin/fund/fund_queries'

export interface FundAssetSelectorProps {
  label: string
  fundId: string
  value: string
  onChange: (value: string) => void
  mode: 'company' | 'external_fund'
  placeholder?: string
  disabled?: boolean
  required?: boolean
  description?: React.ReactNode
  error?: string | boolean
}

interface AssetOption {
  value: string
  label: string
  group: 'in-fund' | 'other'
  fission_ticker?: string
}

export function FundAssetSelector({
  label,
  fundId,
  value,
  onChange,
  mode,
  placeholder,
  disabled = false,
  required = false,
  description,
  error,
}: FundAssetSelectorProps) {
  const { data: assetsData, isLoading } = useAdminFundAssets(fundId)
  const scrollRef = useRef<HTMLDivElement>(null)
  const combobox = useCombobox({
    scrollBehavior: 'auto',
  })

  const fundAssetIds = useMemo(() => {
    if (!assetsData) return []
    return assetsData.fund.assets.nodes.map((node) => node.asset.id)
  }, [assetsData])

  const allAssets = useMemo(() => {
    if (!assetsData) return []
    const targetCategory = mode === 'company' ? 'company' : 'external_fund'
    return assetsData.assets.nodes
      .filter((asset) => asset.category === targetCategory)
      .map(
        (asset) =>
          ({
            value: asset.id,
            label: asset.name,
            group: 'other',
            fission_ticker: asset.fission_ticker || undefined,
          }) as AssetOption,
      )
      .sort((a: AssetOption, b: AssetOption) => a.label.localeCompare(b.label))
  }, [assetsData, mode])

  const fundAssets = useMemo(() => {
    if (!assetsData) return []
    const fundAssetIdsSet = new Set(fundAssetIds)
    return allAssets
      .filter((asset) => fundAssetIdsSet.has(asset.value))
      .map((asset) => ({ ...asset, group: 'in-fund' as const }))
  }, [allAssets, fundAssetIds, assetsData])

  const otherAssets = useMemo(() => {
    const fundAssetIdsSet = new Set(fundAssetIds)
    return allAssets.filter((asset) => !fundAssetIdsSet.has(asset.value))
  }, [allAssets, fundAssetIds])

  const selectedOption = useMemo(() => {
    if (!value) return null
    const fundMatch = fundAssets.find((asset) => asset.value === value)
    if (fundMatch) return fundMatch
    const otherMatch = otherAssets.find((asset) => asset.value === value)
    if (otherMatch) return otherMatch
    return null
  }, [value, fundAssets, otherAssets])

  const filterOptions = (
    options: AssetOption[],
    query: string,
  ): AssetOption[] => {
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(query.toLowerCase().trim()) ||
        option.value.toLowerCase().includes(query.toLowerCase().trim()),
    )
  }

  const filteredFundAssets = useMemo(() => {
    return filterOptions(fundAssets, value)
  }, [fundAssets, value])

  const filteredOtherAssets = useMemo(() => {
    return filterOptions(otherAssets, value)
  }, [otherAssets, value])

  const assetTypeLabel = mode === 'external_fund' ? 'external fund' : 'company'
  const assetTypeLabelPlural =
    mode === 'external_fund' ? 'external funds' : 'companies'

  return (
    <Combobox
      store={combobox}
      withinPortal={false}
      onOptionSubmit={(optionValue) => {
        onChange(optionValue)
        combobox.closeDropdown()
      }}
    >
      <Combobox.Target>
        <TextInput
          label={label}
          placeholder={placeholder || `Select a ${assetTypeLabel}`}
          value={selectedOption ? selectedOption.label : value}
          onChange={(event) => {
            onChange(event.currentTarget.value)
            combobox.resetSelectedOption()
            combobox.openDropdown()
            if (scrollRef.current) {
              scrollRef.current.scrollTop = 0
            }
          }}
          onClick={() => {
            combobox.openDropdown()
          }}
          onFocus={() => {
            combobox.openDropdown()
          }}
          onBlur={() => {
            if (!selectedOption) {
              onChange('')
            }
            combobox.closeDropdown()
          }}
          autoComplete="off"
          rightSection={
            <Group gap="xxs" wrap="nowrap">
              {value && selectedOption && (
                <>
                  <CloseButton
                    aria-label="Clear input"
                    onClick={(e) => {
                      e.stopPropagation()
                      onChange('')
                      combobox.closeDropdown()
                    }}
                    style={{ display: value ? undefined : 'none' }}
                  />
                </>
              )}
            </Group>
          }
          disabled={disabled || isLoading}
          required={required}
          description={description}
          error={error}
        />
      </Combobox.Target>

      <Combobox.Dropdown
        ref={scrollRef}
        style={{ maxHeight: '400px', overflowY: 'auto' }}
      >
        <Combobox.Options>
          {isLoading ? (
            <Combobox.Empty>Loading {assetTypeLabelPlural}...</Combobox.Empty>
          ) : filteredFundAssets.length === 0 &&
            filteredOtherAssets.length === 0 ? (
            <Combobox.Empty>No {assetTypeLabelPlural} found</Combobox.Empty>
          ) : (
            <>
              {filteredFundAssets.length > 0 && (
                <>
                  <Combobox.Group
                    label={
                      mode === 'external_fund'
                        ? 'External funds in fund'
                        : 'Companies in fund'
                    }
                    style={{
                      position: 'sticky',
                      top: 0,
                      background: 'var(--mantine-color-body)',
                      zIndex: 1,
                    }}
                  >
                    {filteredFundAssets.map((item: AssetOption) => (
                      <Combobox.Option value={item.value} key={item.value}>
                        <Group gap="sm">
                          <Text c="dimmed" size="xs">
                            {item.value}
                            {/*Fission Asset ID {item.value}*/}
                            {/*{item.fission_ticker && (*/}
                            {/*  <span style={{ fontFamily: 'monospace', marginLeft: '8px' }}>*/}
                            {/*    ({item.fission_ticker.padEnd(6)})*/}
                            {/*  </span>*/}
                            {/*)}*/}
                          </Text>
                          <Text>{item.label}</Text>
                          {mode === 'company' && <Pill size="xs">In Fund</Pill>}
                        </Group>
                      </Combobox.Option>
                    ))}
                  </Combobox.Group>
                </>
              )}

              {filteredOtherAssets.length > 0 && (
                <Combobox.Group
                  label={`Other ${assetTypeLabelPlural}`}
                  style={{
                    position: 'sticky',
                    top: 0,
                    background: 'var(--mantine-color-body)',
                    zIndex: 1,
                  }}
                >
                  {filteredOtherAssets.map((item: AssetOption) => (
                    <Combobox.Option value={item.value} key={item.value}>
                      <Group gap="sm">
                        <Text c="dimmed" size="xs">
                          {item.value}
                          {/*{item.fission_ticker}*/}
                          {/*{item.fission_ticker && (*/}
                          {/*  <span style={{ fontFamily: 'monospace', marginLeft: '8px' }}>*/}
                          {/*    ({item.fission_ticker.padEnd(6)})*/}
                          {/*  </span>*/}
                          {/*)}*/}
                        </Text>
                        <Text>{item.label}</Text>
                      </Group>
                    </Combobox.Option>
                  ))}
                </Combobox.Group>
              )}
            </>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
